// crewbit-admin/src/components/schedule/WeekCanvasContainer.tsx
//
// Schedule Module Phase C C2 — sibling-component extract of Week-canvas
// wiring (admin; mirrors mobile WeekCanvasContainer + admin DayCanvasContainer
// pattern from C1.6 per Golden Rule #14 surface-thinning).
//
// Owns the conflict-envelope state + reschedule RPC dispatch for cross-column
// drops (date change, time preserved per spec §3.8). Caller passes data slices
// + refresh + toast handler.
//
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §3 + §C.6.

import { useCallback, useState } from 'react';
import { useRescheduleScheduleEntry } from '../../hooks/useRescheduleScheduleEntry';
import { lookupErrorCopy } from '../../lib/errorMap';
import type { ConflictDetail } from '../../lib/errorMap';
import type { ScheduleEntry } from '../../types/schedule';
import { useAuthStore } from '../../stores/authStore';
import { instantToBusinessParts, localWallClockToInstant } from './scheduleHelpers';
import { WeekCanvas } from './WeekCanvas';

export interface WeekCanvasContainerProps {
  effectiveAnchor: Date;
  entries: ScheduleEntry[];
  businessOpen: string;
  businessClose: string;
  onEntryTap: (entryId: string) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onToday: () => void;
  refresh: () => void;
  onToast: (toast: { msg: string; type: 'success' | 'error' }) => void;
}

export function WeekCanvasContainer({
  effectiveAnchor,
  entries,
  businessOpen,
  businessClose,
  onEntryTap,
  onPrevWeek,
  onNextWeek,
  onToday,
  refresh,
  onToast,
}: WeekCanvasContainerProps) {
  const rescheduleEntry = useRescheduleScheduleEntry();
  const [serverConflicts, setServerConflicts] = useState<ConflictDetail[] | null>(null);
  const [pendingReschedule, setPendingReschedule] = useState<{
    entryId: string;
    newStartAt: string;
    newEndAt: string;
  } | null>(null);

  const handleColumnDrop = useCallback(async (newDate: string, droppedEntryId: string) => {
    setServerConflicts(null);
    const entry = entries.find((e) => e.id === droppedEntryId);
    if (!entry) return;
    // Cross-column drop = date change with time preserved per spec §3.8.
    // A2 P4 — preserve the BUSINESS-tz wall-clock when moving to newDate, then
    // rebuild true instants (the old device-tz setFullYear drifted the time on a
    // device whose tz differs from the business tz, and could land on the wrong day).
    const tz = useAuthStore.getState().business?.timezone ?? null;
    const s = instantToBusinessParts(entry.start_at, tz);
    const e = instantToBusinessParts(entry.end_at, tz);
    const pad = (n: number) => String(n).padStart(2, '0');
    const newStartAt = localWallClockToInstant(newDate, `${pad(s.hour)}:${pad(s.minute)}:00`, tz);
    const newEndAt = localWallClockToInstant(newDate, `${pad(e.hour)}:${pad(e.minute)}:00`, tz);
    const result = await rescheduleEntry({
      entryId: droppedEntryId,
      newStartAt,
      newEndAt,
      force: false,
    });
    if (!result.success) {
      if (result.error.code === 'CONFLICT_DETECTED' && result.error.conflicts) {
        setServerConflicts(result.error.conflicts);
        setPendingReschedule({ entryId: droppedEntryId, newStartAt, newEndAt });
      } else {
        onToast({ msg: lookupErrorCopy('reschedule_schedule_entry', result.error.code), type: 'error' });
      }
      return;
    }
    refresh();
    onToast({ msg: 'Rescheduled', type: 'success' });
  }, [entries, rescheduleEntry, refresh, onToast]);

  const handleOverride = useCallback(async () => {
    if (!pendingReschedule) return;
    const result = await rescheduleEntry({
      entryId: pendingReschedule.entryId,
      newStartAt: pendingReschedule.newStartAt,
      newEndAt: pendingReschedule.newEndAt,
      force: true,
    });
    setServerConflicts(null);
    setPendingReschedule(null);
    if (!result.success) {
      onToast({ msg: lookupErrorCopy('reschedule_schedule_entry', result.error.code), type: 'error' });
      return;
    }
    refresh();
    onToast({ msg: 'Rescheduled (override)', type: 'success' });
  }, [pendingReschedule, rescheduleEntry, refresh, onToast]);

  return (
    <WeekCanvas
      date={effectiveAnchor.toISOString().split('T')[0]}
      entries={entries}
      businessOpen={businessOpen}
      businessClose={businessClose}
      onEntryTap={onEntryTap}
      onColumnDrop={handleColumnDrop}
      conflicts={serverConflicts ?? undefined}
      conflictRpcName="reschedule_schedule_entry"
      onConflictOverride={handleOverride}
      onConflictDismiss={() => { setServerConflicts(null); setPendingReschedule(null); }}
      onPrevWeek={onPrevWeek}
      onNextWeek={onNextWeek}
      onToday={onToday}
    />
  );
}
